import React from "react"

// Styles
import useStyles from "./styles"

type ModernDialogProps = {
    open: boolean
    title: string
    onClose?(): void
    actions?: React.ReactNode
    children: React.ReactNode
}

export default function ModernDialog(props: ModernDialogProps) {
    const styles = useStyles();
    const {open, title, onClose, actions, children} = props;
    const cardRef = React.useRef<HTMLDivElement | null>(null);

    /*
     * Take focus on opening, give it back on closing.
     *
     * Without it focus stayed on whatever opened the dialog — over the
     * annotation screen, the canvas behind the overlay — so nothing showed the
     * dialog was the thing the keyboard now spoke to.
     */
    React.useEffect(() => {
        if (!open) {
            return undefined
        }

        const before = document.activeElement as HTMLElement | null;

        if (cardRef.current) {
            cardRef.current.focus()
        }

        return () => {
            if (before && typeof before.focus === "function" && document.body.contains(before)) {
                before.focus()
            }
        }
    }, [open]);

    React.useEffect(() => {
        if (!open || !onClose) {
            return undefined
        }

        function onKeyDown(event: KeyboardEvent) {
            if (event.key === "Escape" && onClose) {
                event.stopPropagation();
                onClose()
            }
        }

        window.addEventListener("keydown", onKeyDown);

        return () => window.removeEventListener("keydown", onKeyDown)
    }, [open, onClose]);

    if (!open) {
        return null
    }

    return (
        <div
            className={styles.overlay}
            onClick={event => {
                if (onClose && event.target === event.currentTarget) {
                    onClose()
                }
            }}
        >
            <div
                ref={cardRef}
                tabIndex={-1}
                className={styles.card}
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className={styles.head}>
                    <span className={styles.title}>{title}</span>
                    {onClose && (
                        <button type="button" className={styles.close} onClick={onClose} aria-label={title}>
                            ×
                        </button>
                    )}
                </div>

                <div className={styles.body}>{children}</div>

                {actions && <div className={styles.actions}>{actions}</div>}
            </div>
        </div>
    )
}
